import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { InteractionStatus } from '@azure/msal-browser';
import { makeStyles, tokens, Spinner, Card, Title2, Body1, Button } from '@fluentui/react-components';
import { DismissCircleRegular } from '@fluentui/react-icons';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: tokens.colorNeutralBackground3,
  },
  card: {
    width: '450px',
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
  },
  icon: {
    fontSize: '64px',
    marginBottom: tokens.spacingVerticalL,
    color: tokens.colorPaletteRedForeground1,
  },
  title: {
    display: 'block',
    marginBottom: tokens.spacingVerticalM,
  },
  message: {
    display: 'block',
    color: tokens.colorNeutralForeground3,
    marginBottom: tokens.spacingVerticalL,
  },
  errorDetails: {
    backgroundColor: tokens.colorNeutralBackground4,
    padding: tokens.spacingVerticalM,
    borderRadius: tokens.borderRadiusMedium,
    marginBottom: tokens.spacingVerticalL,
    textAlign: 'left',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    wordBreak: 'break-word',
  },
});

/**
 * Landing route for a sign-in redirect.
 *
 * MSAL (`handleRedirectPromise` in main.tsx) processes the sign-in response
 * itself; there is no server-side code exchange. This route only waits for
 * MSAL to finish, then sends the operator to the wizard — or shows the error
 * Entra ID returned in the query string.
 */
export function AuthCallback() {
  const styles = useStyles();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { inProgress } = useMsal();

  const errorCode = searchParams.get('error');
  const errorDescription = searchParams.get('error_description');

  useEffect(() => {
    if (!errorCode && inProgress === InteractionStatus.None) {
      navigate('/deploy', { replace: true });
    }
  }, [errorCode, inProgress, navigate]);

  if (!errorCode) {
    return (
      <div className={styles.container}>
        <Card className={styles.card}>
          <Spinner size="large" label="Completing sign-in..." />
        </Card>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <Card className={styles.card}>
        <DismissCircleRegular className={styles.icon} />
        <Title2 className={styles.title}>Sign-in failed</Title2>
        <Body1 className={styles.message}>
          Microsoft Entra ID returned an error. Try signing in again.
        </Body1>
        <div className={styles.errorDetails}>
          <strong>{errorCode}</strong>
          {errorDescription ? `: ${errorDescription}` : ''}
        </div>
        <Button appearance="primary" onClick={() => navigate('/login', { replace: true })}>
          Back to sign-in
        </Button>
      </Card>
    </div>
  );
}
